import { Loader2 } from "lucide-react";

import { type TSectionInitialData } from "@/api/dashboard/dashboard.types";
import { FilterPillsBar } from "@/components/dashboard/FilterPillsBar";
import { Button } from "@/components/ui/Button";
import { useCategorySelection } from "@/hooks/useCategorySelection";
import { cn } from "@/lib/utils";

type TCategoriesFeatureProps = {
  dashboardData: TSectionInitialData | null;
  isLoading: boolean;
  error: string | null;
  hideEmptyState?: boolean;
  companyId?: string | null;
  fullWidth?: boolean;
  onCategorySelectionChange?: (params: { selectedCategoryIds: string[]; isAllSelected: boolean }) => void;
  filterButtons?: TFilterButtonOption[];
  activeFilterButtonId?: string | null;
  onFilterButtonChange?: (filterId: string | null) => void;
  /** When true, do not render the "Categories" label above the pill bar (e.g. for My Deal Network). */
  hideCategoryLabel?: boolean;
};

type TFilterButtonOption = {
  id: string;
  label: string;
};

export function CategoriesFeature({
  dashboardData,
  isLoading,
  error,
  hideEmptyState: _hideEmptyState = false,
  fullWidth = false,
  onCategorySelectionChange,
  filterButtons = [],
  activeFilterButtonId = null,
  onFilterButtonChange,
  hideCategoryLabel = false,
}: TCategoriesFeatureProps) {
  // Hide backend categories whose name collides with a built-in filter button
  // (e.g. a seeded "My Shortlist" category would duplicate the shortlist filter
  // chip — and, unlike the button, it filters by category tag, which no
  // recommendation carries).
  const filterButtonLabels = new Set(filterButtons.map((filter) => filter.label.toLowerCase()));
  const categories = (dashboardData?.categories ?? []).filter(
    (category) => !filterButtonLabels.has(category.categoryName.toLowerCase())
  );
  const { selectedCategoryIds, isAllSelected, handleAllSelect, handleCategoryToggle, resetSelection } =
    useCategorySelection(categories, {
      onSelectionChange: onCategorySelectionChange,
      onFilterReset: () => onFilterButtonChange?.(null),
    });

  const isAllButtonActive = isAllSelected && !activeFilterButtonId;
  const containerClassName = cn("flex h-full flex-col gap-4", fullWidth ? "" : "");

  const handleFilterButtonClick = (filterId: string) => {
    const nextFilterId = activeFilterButtonId === filterId ? null : filterId;
    onFilterButtonChange?.(nextFilterId);
    resetSelection({ skipFilterReset: true });
  };

  return (
    <div>
      <div className={containerClassName}>
        {(categories.length > 0 || filterButtons.length > 0) && (
          <div>
            {!hideCategoryLabel && (
              <div className="flex items-center justify-between pb-2">
                {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground sm:h-4 sm:w-4" /> : null}
              </div>
            )}
            {error ? (
              <p className="text-xs text-destructive sm:text-sm">{error}</p>
            ) : (
              <FilterPillsBar>
                <Button
                  size="sm"
                  variant={isAllButtonActive ? "default" : "outline"}
                  onClick={handleAllSelect}
                  onMouseDown={(e) => e.preventDefault()}
                  disabled={isLoading}
                  className="flex-shrink-0"
                >
                  All
                </Button>
                {filterButtons.map((filter) => (
                  <Button
                    key={filter.id}
                    size="sm"
                    variant={activeFilterButtonId === filter.id ? "default" : "outline"}
                    onClick={() => handleFilterButtonClick(filter.id)}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={isLoading}
                    className="flex-shrink-0"
                  >
                    {filter.label}
                  </Button>
                ))}
                {categories.map((category) => (
                  <Button
                    key={category.categoryId}
                    size="sm"
                    variant={selectedCategoryIds.includes(category.categoryId) ? "default" : "outline"}
                    onClick={() => handleCategoryToggle(category.categoryId)}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={isLoading}
                    className="flex-shrink-0"
                  >
                    {category.categoryName}
                  </Button>
                ))}
              </FilterPillsBar>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
